const apiCMSUrl = process.env.NEXT_PUBLIC_STRAPI_API_URL;
const apiToken = process.env.STRAPI_API_TOKEN;
const apiBaseUrl = process.env.NEXT_PUBLIC_POST_API_URL;

async function fetchData(url) {
    const response = await fetch(url, {
        headers: {
            Authorization: `Bearer ${apiToken}`,
        },
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.status}`);
    }

    const data = await response.json();
    return data;
}

export async function blogLandingPage() {
    if (!apiCMSUrl) {
        throw new Error('NEXT_PUBLIC_POST_API_URL is missing in environment variables');
    }
    const url = `${apiCMSUrl}/blog-landing-page?populate=deep`;
    return await fetchData(url);
}

export async function fetchConfigPage() {
    if (!apiCMSUrl) {
        throw new Error('NEXT_PUBLIC_POST_API_URL is missing in environment variables');
    }
    const url = `${apiCMSUrl}/configurations?populate=deep`;
    return await fetchData(url);
}

export async function fetchHomePage() {
    if (!apiCMSUrl) {
        throw new Error('NEXT_PUBLIC_POST_API_URL is missing in environment variables');
    }
    const url = `${apiCMSUrl}/home-page?populate=deep`;
    return await fetchData(url);
}

export async function fetchCategoryPage(categorySlug) {
    if (!apiCMSUrl) {
        throw new Error('NEXT_PUBLIC_POST_API_URL is missing in environment variables');
    }
    const url = `${apiCMSUrl}/pages-main-categories?populate=deep&[slug_category][slug][$eq]=${categorySlug}`;
    return await fetchData(url);
}

export async function fetchCityPage(categorySlug, locationSlug) {
    if (!apiCMSUrl) {
        throw new Error('NEXT_PUBLIC_POST_API_URL is missing in environment variables');
    }
    const url = `${apiCMSUrl}/pages-cities?populate=deep&[slug_category][slug][$eq]=${categorySlug}&[slug_location][slug][$eq]=${locationSlug}`;
    return await fetchData(url);
}

export async function fetchNeighbourhoodPage(categorySlug, locationSlug, neighbourhoodSlug) {
    if (!apiCMSUrl) {
        throw new Error('NEXT_PUBLIC_POST_API_URL is missing in environment variables');
    }
    let url = `${apiCMSUrl}/page-neighbourhoods?populate=deep`;
    url += `&[slug_category][slug][$eq]=${categorySlug}`;
    url += `&[slug_location][slug][$eq]=${locationSlug}`;
    url += `&[slug_neighbourhood][slug][$eq]=${neighbourhoodSlug}`;
    return await fetchData(url);
}

// Fetch country codes from backend server
async function fetchDataFromBackend(url) {
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.status}`);
    }

    const data = await response.json();
    return data;
}

export async function fetchCountryCodes() {
    if (!apiBaseUrl) {
        throw new Error('NEXT_PUBLIC_POST_API_URL is missing in environment variables');
    }

    try {
        const countryCodeUrl = `${apiBaseUrl}/countries/list?limit=242`;
        const countryCodesData = await fetchDataFromBackend(countryCodeUrl);

        return {
            countries:
                countryCodesData?.data?.countries.map(cCode => {
                    return {
                        code: cCode.dialCode,
                        country: cCode.code,
                    };
                }) ?? [],
            totalQuotes: countryCodesData?.data?.totalQuotes ?? 0,
        };
    } catch (error) {
        // console.error('Error fetching country codes:', error);
        // Return default country codes as fallback
        return {
            countries: [],
            totalQuotes: 0,
        };
    }
}

// Send email with booking information
export async function sendBookingEmail(formData) {
    if (!apiBaseUrl) {
        throw new Error('NEXT_PUBLIC_POST_API_URL is missing in environment variables');
    }

    try {
        const response = await fetch(`${apiBaseUrl}/send/email`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(formData),
        });

        if (!response.ok) {
            throw new Error(`Failed to send email: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error sending booking email:', error);
        throw error;
    }
}

export async function fetchIsBookingEmailEnabled() {
    if (!apiBaseUrl) {
        throw new Error('NEXT_PUBLIC_POST_API_URL is missing in environment variables');
    }

    try {
        const quoteEmail = `${apiBaseUrl}/quote/email/restriction`;
        const response = await fetchDataFromBackend(quoteEmail);
        return {
            isQuoteEmailEnabled: response?.data?.isQuoteEmailEnabled ?? false,
        };
    } catch (error) {
        console.error('Error fetching country codes:', error);
        return {};
    }
}

// Blogs API functions
export async function fetchBlogsPage() {
    if (!apiCMSUrl) {
        throw new Error('NEXT_PUBLIC_STRAPI_API_URL is missing in environment variables');
    }
    const url = `${apiCMSUrl}/pages-blogs?populate=deep&fields[0]=title&fields[1]=Description&fields[2]=Date&fields[3]=ReadTime&fields[4]=slug`;
    return await fetchData(url);
}

export async function fetchBlogs(params = {}) {
    if (!apiCMSUrl) {
        throw new Error('NEXT_PUBLIC_STRAPI_API_URL is missing in environment variables');
    }
    
    const searchParams = new URLSearchParams();
    searchParams.append('populate', '*');
    searchParams.append('status', 'published');
    // Sort latest first by Date then fallback to publishedAt
    // Strapi v4 supports multiple sort params
    searchParams.append('sort[0]', 'Date:desc');
    searchParams.append('sort[1]', 'publishedAt:desc');
    
    // Add pagination
    if (params.page) {
        searchParams.append('pagination[page]', params.page);
    }
    if (params.pageSize) {
        searchParams.append('pagination[pageSize]', params.pageSize);
    }
    
    // Add search filters
    if (params.search) {
        searchParams.append('filters[$or][0][title][$containsi]', params.search);
        searchParams.append('filters[$or][1][Description][$containsi]', params.search);
    }
    
    // Add category filter
    if (params.category && params.category !== 'All') {
        searchParams.append('filters[categories][name][$eq]', params.category);
    }
    
    const url = `${apiCMSUrl}/pages-blogs?${searchParams.toString()}`;

    
    const result = await fetchData(url);

    return result;
}

export async function fetchBlogBySlug(slug) {
    if (!apiCMSUrl) {
        throw new Error('NEXT_PUBLIC_STRAPI_API_URL is missing in environment variables');
    }
    const url = `${apiCMSUrl}/pages-blogs/${slug}?populate=deep`;
    return await fetchData(url);
}

export async function fetchBlogCategories() {
    if (!apiCMSUrl) {
        throw new Error('NEXT_PUBLIC_STRAPI_API_URL is missing in environment variables');
    }
    const url = `${apiCMSUrl}/blog-categories?populate=*`;
    return await fetchData(url);
}

// Author API functions
export async function fetchAllAuthors(params = {}) {
    if (!apiCMSUrl) {
        throw new Error('NEXT_PUBLIC_STRAPI_API_URL is missing in environment variables');
    }
    
    const searchParams = new URLSearchParams();
    searchParams.append('populate[image][fields]', 'url,formats,alternativeText');
    searchParams.append('populate[blogs][fields]', 'id');
    searchParams.append('sort[0]', 'name:asc');
    
    // Add pagination
    if (params.page) {
        searchParams.append('pagination[page]', params.page);
    }
    if (params.pageSize) {
        searchParams.append('pagination[pageSize]', params.pageSize);
    }
    
    const url = `${apiCMSUrl}/blog-authors?${searchParams.toString()}`;
    
    const result = await fetchData(url);
    
    return result;
}

export async function fetchAuthorBySlug(slug, page = 1, pageSize = 9) {
    if (!apiCMSUrl) {
        throw new Error('NEXT_PUBLIC_STRAPI_API_URL is missing in environment variables');
    }
    
    const searchParams = new URLSearchParams();
    searchParams.append('filters[slug][$eq]', slug);
    searchParams.append('pagination[page]', page);
    searchParams.append('pagination[pageSize]', pageSize);
    searchParams.append('populate[image][fields]', 'url,formats,alternativeText');
    searchParams.append('populate[blogs][populate][thumbnail][fields]', 'url,formats,alternativeText');
    searchParams.append('populate[blogs][populate][categories][fields]', 'name,isArchived');
    
    const url = `${apiCMSUrl}/blog-authors?${searchParams.toString()}`;
    
    const result = await fetchData(url);
    
    
    return result;
}
